import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Grid,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  styled,
  FormControlLabel,
  Switch,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import trLocale from 'date-fns/locale/tr';
import enUS from 'date-fns/locale/en-US';
import { useAuth } from '../../contexts/AuthContext';
import * as shiftDataService from '../../services/migration/shiftDataService';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import WorkIcon from '@mui/icons-material/Work';
import EventIcon from '@mui/icons-material/Event';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'; // İkonu import et
import { useTranslation } from 'react-i18next';
import GuideButton from '../common/guide/GuideButton';

// Özel stil tanımlamaları
const InfoItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(1.5),
}));

const InfoIcon = styled(Box)(({ theme }) => ({
  color: theme.palette.primary.main,
  marginRight: theme.spacing(1.5),
  display: 'flex',
  alignItems: 'center',
}));

interface VardiyaZamanDuzenleModalProps {
  open: boolean;
  onClose: () => void;
  report: any | null;
}

const VardiyaZamanDuzenleModal: React.FC<VardiyaZamanDuzenleModalProps> = ({
  open,
  onClose,
  report
}) => {
  const { t, i18n } = useTranslation(['humanResources', 'common2']);
  const isEnglish = i18n.language?.toLowerCase().startsWith('en');
  const tr = (trText: string, enText: string) => (isEnglish ? enText : trText);
  const hr = (key: string, defaultValue: string) => t(key, { ns: 'humanResources', defaultValue });
  const { userDetails } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [girisZamani, setGirisZamani] = useState<Date | null>(null);
  const [cikisZamani, setCikisZamani] = useState<Date | null>(null);
  
  // Konum bilgileri için state'ler
  const [girisKonum, setGirisKonum] = useState<any | null>(null);
  const [cikisKonum, setCikisKonum] = useState<any | null>(null);

  // Özel tarih kontrolü için state'ler
  const [girisOzelTarih, setGirisOzelTarih] = useState<Date | null>(null);
  const [cikisOzelTarih, setCikisOzelTarih] = useState<Date | null>(null);
  
  // Özel tarih kullanımını kontrol eden switch'ler
  const [girisOzelTarihAktif, setGirisOzelTarihAktif] = useState(false);
  const [cikisOzelTarihAktif, setCikisOzelTarihAktif] = useState(false);
  
  // Modal açıldığında verileri yükle
  useEffect(() => {
    if (open && report) {
      resetForm();
      
      // Rapor tarihini varsayılan olarak ayarla (dd-MM-yyyy formatından)
      const dateParts = report.date.split('-');
      if (dateParts.length === 3) {
        const day = parseInt(dateParts[0], 10);
        const month = parseInt(dateParts[1], 10) - 1; // Ay 0-11 arası
        const year = parseInt(dateParts[2], 10);
        const reportDate = new Date(year, month, day);
        
        setGirisOzelTarih(reportDate);
        setCikisOzelTarih(reportDate);
      }
      
      // Giriş zamanını ayarla
      if (report.originalRecord.girisZamani) {
        const girisDate = new Date(report.originalRecord.girisZamani);
        setGirisZamani(girisDate);
      }
      
      // Çıkış zamanını ayarla
      if (report.originalRecord.cikisZamani) {
        const cikisDate = new Date(report.originalRecord.cikisZamani);
        setCikisZamani(cikisDate);
      }
      
      // Konumları ayarla
      setGirisKonum(report.originalRecord.girisKonum || null);
      setCikisKonum(report.originalRecord.cikisKonum || null);
    }
  }, [open, report]);
  
  // Formu sıfırla
  const resetForm = () => {
    setGirisZamani(null);
    setCikisZamani(null);
    setGirisKonum(null);
    setCikisKonum(null);
    setGirisOzelTarih(null);
    setCikisOzelTarih(null);
    setGirisOzelTarihAktif(false);
    setCikisOzelTarihAktif(false);
    setSuccess(false);
    setError(null);
  };
  
  // Zamanları ve konumları değiştirme fonksiyonu
  const handleSwapTimes = () => {
    // Her ikisi de doluysa, tüm verileri (saat, tarih, switch, konum) yer değiştir
    if (girisZamani && cikisZamani) {
      // Saatler
      const tempGirisZamani = girisZamani;
      setGirisZamani(cikisZamani);
      setCikisZamani(tempGirisZamani);
      // Konumlar
      const tempGirisKonum = girisKonum;
      setGirisKonum(cikisKonum);
      setCikisKonum(tempGirisKonum);
      // Tarihler
      const tempGirisTarih = girisOzelTarih;
      setGirisOzelTarih(cikisOzelTarih);
      setCikisOzelTarih(tempGirisTarih);
      // Switch'ler
      const tempGirisAktif = girisOzelTarihAktif;
      setGirisOzelTarihAktif(cikisOzelTarihAktif);
      setCikisOzelTarihAktif(tempGirisAktif);
    }
    // Sadece giriş doluysa, tüm giriş verilerini çıkışa taşı ve girişi boşalt
    else if (girisZamani && !cikisZamani) {
      setCikisZamani(girisZamani);
      setGirisZamani(null);
      setCikisKonum(girisKonum);
      setGirisKonum(null);
      setCikisOzelTarih(girisOzelTarih);
      setGirisOzelTarih(null);
      setCikisOzelTarihAktif(girisOzelTarihAktif);
      setGirisOzelTarihAktif(false);
    }
    // Sadece çıkış doluysa, tüm çıkış verilerini girişe taşı ve çıkışı boşalt
    else if (!girisZamani && cikisZamani) {
      setGirisZamani(cikisZamani);
      setCikisZamani(null);
      setGirisKonum(cikisKonum);
      setCikisKonum(null);
      setGirisOzelTarih(cikisOzelTarih);
      setCikisOzelTarih(null);
      setGirisOzelTarihAktif(cikisOzelTarihAktif);
      setCikisOzelTarihAktif(false);
    }
  };

  // DUAL: giriş/çıkış punch'larını yön bazlı düzenle/ekle/sil.
  // - id var + zaman var  → editPunch (tarih+saat)
  // - id var + zaman yok  → deletePunch (temizle)
  // - id yok + zaman var  → addPunch (eksik giriş/çıkış tamamla → tam oturum)
  const saveDualPunches = async (checkInTime: string | null, checkOutTime: string | null) => {
    const shiftId = report.shiftId != null ? Number(report.shiftId) : null;
    const shiftName = report.shift || null;

    // Giriş yönü
    if (report.punchInId) {
      if (checkInTime) {
        await shiftDataService.editPunch({ id: Number(report.punchInId), time: checkInTime, direction: 'in', status: 'onTime' });
      } else {
        await shiftDataService.deletePunch(Number(report.punchInId));
      }
    } else if (checkInTime) {
      await shiftDataService.addPunch({
        uid: report.personnelId,
        direction: 'in',
        time: checkInTime,
        shiftId,
        shiftName,
        status: 'onTime',
      });
    }

    // Çıkış yönü
    if (report.punchOutId) {
      if (checkOutTime) {
        await shiftDataService.editPunch({ id: Number(report.punchOutId), time: checkOutTime, direction: 'out', status: 'complete' });
      } else {
        await shiftDataService.deletePunch(Number(report.punchOutId));
      }
    } else if (checkOutTime) {
      await shiftDataService.addPunch({
        uid: report.personnelId,
        direction: 'out',
        time: checkOutTime,
        shiftId,
        shiftName,
        status: 'complete',
      });
    }
  };

  // Kayıt işlemi
  const handleSave = async () => {
    if (!userDetails?.companyId || !report) {
      setError(hr('shiftTimeModal.errors.authorization', 'Authorization error. Please sign in again.'));
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Tarih ve personel ID bilgilerini al
      const dateParts = report.date.split('-');
      if (dateParts.length !== 3) {
        throw new Error('Geçersiz tarih formatı.');
      }
      
      // Rapor tarihini ayrıştır (dd-MM-yyyy formatından)
      const day = parseInt(dateParts[0], 10);
      const month = parseInt(dateParts[1], 10) - 1; // Ay 0-11 arası olduğu için -1
      const year = parseInt(dateParts[2], 10);
      const reportDate = new Date(year, month, day); 
      
      // Giriş zamanı (TR yerel instant → ISO/UTC). Boşsa null (temizle).
      let checkInTime: string | null = null;
      if (girisZamani) {
        let girisDate: Date;
        if (girisOzelTarihAktif && girisOzelTarih) {
          girisDate = new Date(girisOzelTarih);
          girisDate.setHours(girisZamani.getHours());
          girisDate.setMinutes(girisZamani.getMinutes());
        } else {
          const baseDate = new Date(reportDate);
          baseDate.setHours(girisZamani.getHours());
          baseDate.setMinutes(girisZamani.getMinutes());
          girisDate = baseDate;
        }
        girisDate.setSeconds(0, 0);
        checkInTime = girisDate.toISOString();
      }
      
      // Çıkış zamanı
      let checkOutTime: string | null = null;
      if (cikisZamani) {
        let cikisDate: Date;
        if (cikisOzelTarihAktif && cikisOzelTarih) {
          cikisDate = new Date(cikisOzelTarih);
          cikisDate.setHours(cikisZamani.getHours());
          cikisDate.setMinutes(cikisZamani.getMinutes());
        } else {
          const baseDate = new Date(reportDate);
          // Giriş ve çıkış saatleri gün aşıyorsa çıkış tarihini bir gün artır
          if (girisZamani && cikisZamani.getHours() < girisZamani.getHours()) {
            baseDate.setDate(baseDate.getDate() + 1);
          }
          baseDate.setHours(cikisZamani.getHours());
          baseDate.setMinutes(cikisZamani.getMinutes());
          cikisDate = baseDate;
        }
        cikisDate.setSeconds(0, 0);
        checkOutTime = cikisDate.toISOString();
      }

      // Tarihi yyyy-MM-dd'ye çevir (API ISO format bekler)
      const isoDate = report.attendanceDateIso || `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;

      if (report.attendanceMode === 'dual') {
        // DUAL: oturum punch modeli — her yön ayrı ShiftPunch olarak düzenlenir/eklenir/silinir.
        await saveDualPunches(checkInTime, checkOutTime);
      } else {
        // SINGLE: Manuel düzenleme (SADECE SQL — gts_hr_api). setCheckIn/Out=true → null olsa bile uygula (temizle).
        await shiftDataService.editAttendance({
          uid: report.personnelId,
          date: isoDate,
          setCheckIn: true,
          setCheckOut: true,
          checkInTime,
          checkInStatus: girisZamani ? 'onTime' : 'Giriş Yapılmamış',
          checkInLocationJson: girisKonum ? JSON.stringify(girisKonum) : null,
          checkOutTime,
          checkOutStatus: cikisZamani ? 'complete' : 'Çıkış Yapılmamış',
          checkOutLocationJson: cikisKonum ? JSON.stringify(cikisKonum) : null,
        });
      }

      // Başarılı mesajı göster
      setSuccess(true);
      // 2 saniye sonra modalı kapat
      setTimeout(() => {
        onClose();
      }, 2000);
      
    } catch (err) {
      console.error('Vardiya bilgisi güncellenirken hata:', err);
      setError((err as Error).message || 'Bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };
  
  // Modalı kapatma işlemi
  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };
  
  // Eğer rapor yoksa boş içerik göster
  if (!report) {
    return null;
  }
  
  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        pb: 1,
        pt: isMobile ? 2 : 2,
        px: isMobile ? 2 : 3
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <EditIcon sx={{ mr: 1, color: 'primary.main', fontSize: isMobile ? '1.2rem' : '1.5rem' }} />
          <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight="bold">
            {hr('shiftTimeModal.title', 'Edit Shift Times')}
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <GuideButton guideKey="shifts.timeEdit" />
        <IconButton 
          onClick={handleClose} 
          disabled={loading}
          sx={{ 
            color: 'text.secondary',
            '&:hover': { color: 'error.main' } 
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <Divider />
      
      <DialogContent sx={{ pt: isMobile ? 2 : 3, px: isMobile ? 2 : 3 }}>
        <Grid container spacing={isMobile ? 2 : 3}>
          {/* Personel ve Vardiya Bilgileri */}
          <Grid item xs={12} md={6}>
            <Box sx={{ 
              p: isMobile ? 1.5 : 2, 
              bgcolor: 'background.default', 
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
            }}>
              <Typography variant={isMobile ? "subtitle2" : "subtitle1"} fontWeight="bold" sx={{ mb: isMobile ? 1.5 : 2 }}>
                {hr('shiftTimeModal.sections.personnelShiftInfo', 'Personnel and Shift Information')}
              </Typography>
              
              {/* Mobilde kompakt bilgi gösterimi */}
              {isMobile ? (
                <Box>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <PersonIcon sx={{ fontSize: '1rem', color: 'primary.main', mr: 1 }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1 }}>
                            {hr('shiftTimeModal.labels.personnel', 'Personnel')}
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {report.personnel}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <WorkIcon sx={{ fontSize: '1rem', color: 'primary.main', mr: 1 }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1 }}>
                            {hr('shiftTimeModal.labels.shift', 'Shift')}
                          </Typography>
                          <Typography variant="body2">
                            {report.shift}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CalendarTodayIcon sx={{ fontSize: '1rem', color: 'primary.main', mr: 1 }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1 }}>
                            {hr('shiftTimeModal.labels.date', 'Date')}
                          </Typography>
                          <Typography variant="body2">
                            {report.date}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                  
                  <Divider sx={{ my: 1.5 }} />
                  
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    {hr('shiftTimeModal.labels.currentStatus', 'Current Status')}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {report.statusInfo.entryStatus && (
                      <Chip 
                        label={report.statusInfo.entryStatus}
                        size="small"
                        sx={{ height: 24, fontSize: '0.7rem' }}
                        color={
                          report.statusInfo.entryStatus.includes('Normal') || 
                          report.statusInfo.entryStatus.includes('Erken') 
                            ? 'success' 
                            : report.statusInfo.entryStatus.includes('Geç') 
                              ? 'warning' 
                              : 'error'
                        }
                      />
                    )}
                    
                    {report.statusInfo.exitStatus && (
                      <Chip 
                        label={report.statusInfo.exitStatus}
                        size="small"
                        sx={{ height: 24, fontSize: '0.7rem' }}
                        color={
                          report.statusInfo.exitStatus === 'Vardiya Tamamlandı' || 
                          report.statusInfo.exitStatus === 'Normal Çıktı'
                            ? 'success'
                            : report.statusInfo.exitStatus === 'Erken Çıktı'
                              ? 'warning'
                              : report.statusInfo.exitStatus === 'Devam Ediyor'
                                ? 'info'
                                : 'error'
                        }
                      />
                    )}
                  </Box>
                </Box>
              ) : (
                /* Desktop görünümü */
                <>
                  <InfoItem>
                    <InfoIcon>
                      <PersonIcon />
                    </InfoIcon>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {hr('shiftTimeModal.labels.personnelName', 'Personnel Name')}
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {report.personnel}
                      </Typography>
                    </Box>
                  </InfoItem>
                  
                  <InfoItem>
                    <InfoIcon>
                      <WorkIcon />
                    </InfoIcon>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {hr('shiftTimeModal.labels.shift', 'Shift')}
                      </Typography>
                      <Typography variant="body1">
                        {report.shift}
                      </Typography>
                    </Box>
                  </InfoItem>
                  
                  <InfoItem>
                    <InfoIcon>
                      <CalendarTodayIcon />
                    </InfoIcon>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {hr('shiftTimeModal.labels.recordDate', 'Record Date')}
                      </Typography>
                      <Typography variant="body1">
                        {report.date}
                      </Typography>
                    </Box>
                  </InfoItem>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {hr('shiftTimeModal.labels.currentStatus', 'Current Status')}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {report.statusInfo.entryStatus && (
                      <Chip 
                        label={report.statusInfo.entryStatus}
                        size="small"
                        color={
                          report.statusInfo.entryStatus.includes('Normal') || 
                          report.statusInfo.entryStatus.includes('Erken') 
                            ? 'success' 
                            : report.statusInfo.entryStatus.includes('Geç') 
                              ? 'warning' 
                              : 'error'
                        }
                      />
                    )}
                    
                    {report.statusInfo.exitStatus && (
                      <Chip 
                        label={report.statusInfo.exitStatus}
                        size="small"
                        color={
                          report.statusInfo.exitStatus === 'Vardiya Tamamlandı' || 
                          report.statusInfo.exitStatus === 'Normal Çıktı'
                            ? 'success'
                            : report.statusInfo.exitStatus === 'Erken Çıktı'
                              ? 'warning'
                              : report.statusInfo.exitStatus === 'Devam Ediyor'
                                ? 'info'
                                : 'error'
                        }
                      />
                    )}
                  </Box>
                </>
              )}
            </Box>
          </Grid>
          
          {/* Zaman Düzenleme Formu */}
          <Grid item xs={12} md={6}>
            <Box sx={{ 
              p: isMobile ? 1.5 : 2, 
              bgcolor: 'primary.light', 
              color: 'white', 
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              position: 'relative'
            }}>
              <Typography variant={isMobile ? "subtitle2" : "subtitle1"} fontWeight="bold" sx={{ mb: isMobile ? 2 : 3 }}>
                {hr('shiftTimeModal.sections.editTimes', 'Edit Check-In and Check-Out Times')}
              </Typography>

              {report.attendanceMode === 'dual' && report.anomaly && (
                <Alert severity="warning" sx={{ mb: 2, backgroundColor: 'rgba(255,255,255,0.92)' }}>
                  {report.anomaly === 'orphan_out'
                    ? tr('Girişsiz çıkış: giriş saatini girip kaydederek oturumu tamamlayabilirsiniz.', 'Check-out without check-in: enter a check-in time and save to complete the session.')
                    : tr('Çıkış eksik: çıkış saatini girip kaydederek oturumu tamamlayabilirsiniz.', 'Missing check-out: enter a check-out time and save to complete the session.')}
                </Alert>
              )}
              
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={isEnglish ? enUS : trLocale}>
                {/* Mobilde yan yana, desktop'ta alt alta */}
                <Grid container spacing={isMobile ? 1 : 0}>
                  {/* Giriş Zamanı Bölümü */}
                  <Grid item xs={12} sm={isMobile ? 6 : 12}>
                    <Box sx={{ mb: isMobile ? 0 : 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <LoginIcon fontSize="small" sx={{ mr: 1 }} />
                        <Typography variant={isMobile ? "caption" : "body2"} fontWeight="medium">
                          {hr('shiftTimeModal.labels.checkInTime', 'Check-In Time')}
                        </Typography>
                      </Box>
                      
                      <TimePicker
                        value={girisZamani}
                        onChange={(newValue) => setGirisZamani(newValue)}
                        ampm={false}
                        views={['hours', 'minutes']}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small",
                            sx: {
                              backgroundColor: 'rgba(255,255,255,0.9)',
                              borderRadius: 1,
                              '& .MuiInputBase-input': {
                                fontSize: isMobile ? '0.9rem' : '1rem'
                              }
                            }
                          }
                        }}
                      />
                      
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'rgba(255,255,255,0.8)', fontSize: isMobile ? '0.65rem' : '0.75rem' }}>
                        {report.checkIn !== '--:--' ? `${hr('shiftTimeModal.labels.current', 'Current')}: ${report.checkIn}` : hr('shiftTimeModal.labels.noCheckInRecord', 'No check-in record')}
                      </Typography>
                      
                      {/* Giriş için özel tarih seçimi */}
                      <FormControlLabel
                        control={
                          <Switch 
                            checked={girisOzelTarihAktif}
                            onChange={(e) => setGirisOzelTarihAktif(e.target.checked)}
                            size="small"
                            color="default"
                          />
                        }
                        label={
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)', fontSize: isMobile ? '0.65rem' : '0.75rem' }}>
                            {hr('shiftTimeModal.labels.differentDate', 'Different date')}
                          </Typography>
                        }
                        sx={{ mt: 0.5, mb: 0.5 }}
                      />
                      
                      {girisOzelTarihAktif && (
                        <Box sx={{ mt: 0.5 }}>
                          <DatePicker
                            value={girisOzelTarih}
                            onChange={(newValue) => setGirisOzelTarih(newValue)}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                size: "small",
                                sx: {
                                  backgroundColor: 'rgba(255,255,255,0.9)',
                                  borderRadius: 1,
                                  '& .MuiInputBase-input': {
                                    fontSize: isMobile ? '0.75rem' : '0.85rem'
                                  }
                                }
                              }
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Grid>
                  
                  {/* Zaman Değiştirme Butonu - Mobilde gizle, altta göster */}
                  {!isMobile && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                        <IconButton 
                          onClick={handleSwapTimes}
                          sx={{
                            border: '1px solid rgba(255,255,255,0.5)',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'rgba(255,255,255,0.2)'
                            }
                          }}
                        >
                          <SwapHorizIcon />
                        </IconButton>
                      </Box>
                    </Grid>
                  )}

                  {/* Çıkış Zamanı Bölümü */}
                  <Grid item xs={12} sm={isMobile ? 6 : 12}>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                        <Typography variant={isMobile ? "caption" : "body2"} fontWeight="medium">
                          {hr('shiftTimeModal.labels.checkOutTime', 'Check-Out Time')}
                        </Typography>
                      </Box>
                      
                      <TimePicker
                        value={cikisZamani}
                        onChange={(newValue) => setCikisZamani(newValue)}
                        ampm={false}
                        views={['hours', 'minutes']}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small",
                            sx: {
                              backgroundColor: 'rgba(255,255,255,0.9)',
                              borderRadius: 1,
                              '& .MuiInputBase-input': {
                                fontSize: isMobile ? '0.9rem' : '1rem'
                              }
                            }
                          }
                        }}
                      />
                      
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'rgba(255,255,255,0.8)', fontSize: isMobile ? '0.65rem' : '0.75rem' }}>
                        {report.checkOut !== '--:--' ? `${hr('shiftTimeModal.labels.current', 'Current')}: ${report.checkOut}` : hr('shiftTimeModal.labels.noCheckOutRecord', 'No check-out record')}
                      </Typography>
                      
                      {/* Çıkış için özel tarih seçimi */}
                      <FormControlLabel
                        control={
                          <Switch 
                            checked={cikisOzelTarihAktif}
                            onChange={(e) => setCikisOzelTarihAktif(e.target.checked)}
                            size="small"
                            color="default"
                          />
                        }
                        label={
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)', fontSize: isMobile ? '0.65rem' : '0.75rem' }}>
                            {hr('shiftTimeModal.labels.differentDate', 'Different date')}
                          </Typography>
                        }
                        sx={{ mt: 0.5, mb: 0.5 }}
                      />
                      
                      {cikisOzelTarihAktif && (
                        <Box sx={{ mt: 0.5 }}>
                          <DatePicker
                            value={cikisOzelTarih}
                            onChange={(newValue) => setCikisOzelTarih(newValue)}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                size: "small",
                                sx: {
                                  backgroundColor: 'rgba(255,255,255,0.9)',
                                  borderRadius: 1,
                                  '& .MuiInputBase-input': {
                                    fontSize: isMobile ? '0.75rem' : '0.85rem'
                                  }
                                }
                              }
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Grid>
                  
                  {/* Mobilde Zaman Değiştirme Butonu */}
                  {isMobile && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                        <IconButton 
                          onClick={handleSwapTimes}
                          size="small"
                          sx={{
                            border: '1px solid rgba(255,255,255,0.5)',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'rgba(255,255,255,0.2)'
                            }
                          }}
                        >
                          <SwapHorizIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </LocalizationProvider>
              
              {/* Başarı mesajı */}
              {success && (
                <Alert severity="success" sx={{ mt: 3, backgroundColor: 'rgba(255,255,255,0.9)' }}>
                  {hr('shiftTimeModal.messages.updated', 'Shift time updated successfully!')}
                </Alert>
              )}
              
              {/* Hata mesajı */}
              {error && (
                <Alert severity="error" sx={{ mt: 3, backgroundColor: 'rgba(255,255,255,0.9)' }}>
                  {error}
                </Alert>
              )}
              
              {loading && (
                <Box sx={{ 
                  position: 'absolute', 
                  top: 0, 
                  left: 0, 
                  right: 0, 
                  bottom: 0,
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  backgroundColor: 'rgba(0,0,0,0.3)',
                  borderRadius: 2
                }}>
                  <CircularProgress color="inherit" />
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ 
        px: isMobile ? 2 : 3, 
        py: isMobile ? 1.5 : 2,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 1 : 0
      }}>
        {isMobile ? (
          <>
            <Button 
              onClick={handleSave}
              variant="contained" 
              color="primary"
              disabled={loading}
              fullWidth
              startIcon={<AccessTimeIcon />}
              size="large"
            >
              {loading ? hr('shiftTimeModal.saving', 'Saving...') : hr('shiftTimeModal.saveTimes', 'Save Times')}
            </Button>
            <Button 
              onClick={handleClose} 
              color="inherit" 
              disabled={loading}
              fullWidth
            >
              {hr('common.cancel', 'Cancel')}
            </Button>
          </>
        ) : (
          <>
            <Button 
              onClick={handleClose} 
              color="inherit" 
              disabled={loading}
            >
              {hr('common.cancel', 'Cancel')}
            </Button>
            <Button 
              onClick={handleSave}
              variant="contained" 
              color="primary"
              disabled={loading}
              startIcon={<AccessTimeIcon />}
            >
              {loading ? hr('shiftTimeModal.saving', 'Saving...') : hr('shiftTimeModal.saveTimes', 'Save Times')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default VardiyaZamanDuzenleModal; 